import {
  Component,
  ElementRef,
  HostListener,
  Input,
  ViewChild,
  effect,
  signal,
  computed,
  input,
  AfterViewInit,
  OnDestroy,
} from '@angular/core';
import { NgIf, NgForOf } from '@angular/common';
import { CommonModule } from '@angular/common';
import { NgSelectComponent, NgSelectModule } from '@ng-select/ng-select';
import { FormsModule } from '@angular/forms';
import {
  MatDatepicker,
  MatDatepickerModule,
} from '@angular/material/datepicker';
import { MatDatepickerInput } from '@angular/material/datepicker';
import { MatIconModule } from '@angular/material/icon';
import { MatNativeDateModule } from '@angular/material/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute } from '@angular/router';
import { ApiService } from '@app/core/services/API/api-service';
import { ToastrService } from 'ngx-toastr';
import { ConfirmPopup } from '@app/shared/components/confirm-popup/confirm-popup';
import {
  AssigneeSelectionSlider,
  AssigneeUser,
} from '@app/shared/components/assignee-selection-slider/assignee-selection-slider';
import {
  ApprovalService,
  LogTimeService,
  ProjectsService,
  TaskService,
} from '@app/pages/projects/projects.service';
import { Spinner } from '@app/shared/components/spinner/spinner';
import { StatusSidebarRefreshService } from '@app/core/services/sidebar/status-sidebar-refresh/status-sidebar-refresh.service';
import { CrossTabSyncService } from '@app/core/services/cross-tab-sync/cross-tab-sync.service';
import { ProjectHoursService } from '@app/core/services/sidebar/project-hours.service';
import { fromEvent, Subject, interval } from 'rxjs';
import { debounceTime, takeUntil, catchError, distinctUntilChanged, filter } from 'rxjs/operators';
import * as Highcharts from 'highcharts';
import { ApprovalRefreshService } from '@app/core/services/approval-refresh.service/approval-refresh.service';

interface Step {
  label: string;
  status: string;
}

interface StatusConfig {
  type: 'task' | 'issue' | 'project' | 'revision' | 'request' | 'custom';
  title: string;
  statusLabel: string;
  assignLabel: string;
  plannedHoursLabel?: string;
  dueDateLabel?: string;
  refNumberLabel?: string;
  showPlannedHours?: boolean;
  showDueDate?: boolean;
  showRefNumber?: boolean;
  showApprovals?: boolean;
  showTimeSummary?: boolean;
  customFields?: any[];
}

@Component({
  selector: 'app-status-sidebar',
  standalone: true,
  imports: [
    CommonModule,
    NgSelectModule,
    FormsModule,
    MatDatepickerInput,
    MatDatepicker,
    MatDatepickerModule,
    MatNativeDateModule,
    MatIconModule,
    MatTooltipModule,
    ConfirmPopup,
    AssigneeSelectionSlider,
    Spinner,
    NgIf,
    NgForOf,
  ],
  templateUrl: './status-sidebar.html',
  styleUrl: './status-sidebar.scss',
})
export class StatusSidebar implements AfterViewInit, OnDestroy {
  // Configurable inputs
  itemId = input<string | null>(null);
  config = input.required<StatusConfig>();
  service = input.required<any>(); // Service to handle API calls
  updateMethod = input.required<string>(); // Method name for updating item
  getDetailsMethod = input.required<string>(); // Method name for getting item details
  refreshTrigger = input<number>(0);

  // State signals
  selectedTab = signal<'manage_item' | 'time_summary'>('manage_item');
  selectedStatus = signal<string | null>('Not Selected');
  selectedApproval = signal<string | null>(null);
  selectedStatusApproval = signal<string | null>(null);
  itemDetails = signal<any>(null);

  showAssignModal = signal(false);
  showApprovalsModal = signal(false);
  assignedUsers = signal<AssigneeUser[]>([]);
  allAssignableData = signal<AssigneeUser[]>([]);
  statusList = signal<any[]>([]);
  statusListApproval = signal<any[]>([]);
  approvalList = signal<any[]>([]);
  isLoadingData = signal(false);
  steps = signal<Step[]>([]);
  userPreference = signal<any>(null);
  approvalPaths = signal<any>(null);
  private _activeStep = signal(0);
  private _visibleStepStart = signal(0);
  selectedStatusValue = signal<any>(null);
  displayApprovalRejectButton = signal(false);
  approverID = signal<string | null>(null);

  // Add signals for remove confirmation
  showRemoveConfirmPopup = signal(false);
  userToRemove = signal<AssigneeUser | null>(null);
  showDeleteApprovalConfirmPopup = signal(false);
  approvalToDelete = signal<any>(null);

  // Planned hours and date signals
  showDivCopy = signal(false);
  showInput = signal(false);
  inputValue = signal('');
  submittedValue = signal('');
  originalPlannedHours = signal('');
  childExist = signal(false);
  selectedDate = signal<Date | null>(null);
  showDiv = signal(false);
  isUpdatingPlannedHours = signal(false);

  // Add signals for drag detection
  private isDragging = signal(false);
  private mouseDownPosition = signal<{ x: number; y: number } | null>(null);

  // Add this signal to store the pathStartStatusList
  pathStartStatusList = signal<string[]>([]);

  // Add signals for approvals modal
  assignList = signal<any[]>([]);
  isLoadingApprovals = signal(false);
  isSavingApproval = signal(false);
  isApprovingApproval = signal(false);
  isRejectingApproval = signal(false);
  singleApprovalData = signal<any>(null);
  approvalExists = signal<boolean>(false); // Track if approval exists based on API response
  
  // Validation signals for approvals
  approvalValidationErrors = signal<string[]>([]);
  showApprovalValidationErrors = signal(false);
  approvalInvalidFields = signal<Set<string>>(new Set());
  // Trigger signal to force computed validation to recompute when approval data is mutated
  approvalDataChangeTrigger = signal(0);

  // Task percentage for time summary
  taskPercentage = signal<number>(0);

  // Time summary data signals
  taskHourData = signal<any>(null);
  taskHourStatus = signal<any>(null);
  isTimeSummaryLoading = signal<boolean>(false);
  isChartsLoading = signal<boolean>(false);
  hasChartError = signal<boolean>(false);

  // Chart instances and timeouts
  private chartInstances: any[] = [];
  private loadingTimeout: any = null;
  private fallbackTimeout: any = null;
  private destroy$ = new Subject<void>();
  private lastProcessedDataHash: string | null = null;
  private isProcessingCacheUpdate = false;
  private lastApprovalRefreshTrigger = 0;

  // Helper method to check if status is complete/closed
  isStatusComplete(): boolean {
    const statusName = this.selectedStatusValue()?.toLowerCase();
    return statusName === 'complete' || statusName === 'closed';
  }

  // ViewChilds
  @ViewChild('inputBox') inputBox!: ElementRef;
  @ViewChild('picker') picker!: MatDatepicker<Date>;
  @ViewChild('taskStatusChartContainer') taskStatusChartContainer!: ElementRef<HTMLDivElement>;
  @ViewChild('balanceHoursChartContainer') balanceHoursChartContainer!: ElementRef<HTMLDivElement>;

  constructor(
    private route: ActivatedRoute,
    private apiService: ApiService,
    private toastr: ToastrService,
    private projectsService: ProjectsService,
    private approvalService: ApprovalService,
    private statusSidebarRefreshService: StatusSidebarRefreshService,
    private logtimeService: LogTimeService,
    private crossTabSyncService: CrossTabSyncService,
    private projectHoursService: ProjectHoursService,
    private approvalRefreshService: ApprovalRefreshService
  ) {
    // Initialization effect
    effect(() => {
      const id = this.itemId();
      if (id) {
        // Reset approvalExists when item changes
        this.approvalExists.set(false);
        this.getItemDetails(id);
        this.getAssignList();
        this.getStatus();
        this.getApprovals();
        this.getApprovalTracking(id);
        this.loadAssignList();
      }
    });
    // Refresh trigger effect
    effect(() => {
      const trigger = this.refreshTrigger();
      const id = this.itemId();
      if (trigger > 0 && id) {
        // Clear cache and refresh data when trigger changes
        if (this.service().clearSingleTaskCache) {
          this.service().clearSingleTaskCache(id);
        }
        if (this.service().clearSingleIssueCache) {
          this.service().clearSingleIssueCache(id);
        }
        if (this.service().clearSingleProjectCache) {
          this.service().clearSingleProjectCache(id);
        }
        if (this.service().clearSingleRevisionCache) {
          this.service().clearSingleRevisionCache(id);
        }
        if (this.service().clearSingleRequestCache) {
          this.service().clearSingleRequestCache(id);
        }

        // Refresh the item details
        this.getItemDetails(id);
        this.getApprovalTracking(id);
      }
    });

    // Approval refresh effect - listen for changes from approvals component
    effect(() => {
      const approvalRefreshTrigger = this.approvalRefreshService.refreshTrigger();
      const id = this.itemId();
      if (approvalRefreshTrigger > this.lastApprovalRefreshTrigger && id) {
        this.lastApprovalRefreshTrigger = approvalRefreshTrigger;
        // Clear cache before refreshing to ensure fresh data
        if (this.service().clearSingleTaskCache) {
          this.service().clearSingleTaskCache(id);
        }
        if (this.service().clearSingleIssueCache) {
          this.service().clearSingleIssueCache(id);
        }
        if (this.service().clearSingleProjectCache) {
          this.service().clearSingleProjectCache(id);
        }
        if (this.service().clearSingleRevisionCache) {
          this.service().clearSingleRevisionCache(id);
        }
        if (this.service().clearSingleRequestCache) {
          this.service().clearSingleRequestCache(id);
        }
        // Refresh approval-related data when approvals component makes changes
        this.getApprovals();
        // getItemDetails will handle calling getSingleApproval if approvalProcess exists
        // Don't check selectedApproval here as it might have stale data
        this.getItemDetails(id);
        this.getApprovalTracking(id);
      }
    });

  }

  visibleSteps = computed(() => {
    const steps = this.steps();
    if (steps.length <= 3) return steps;
    const start = Math.max(
      0,
      Math.min(this.visibleStepStart, steps.length - 3)
    );
    return steps.slice(start, start + 3);
  });

  canGoPrev = computed(() => this.activeStep > 0);
  canGoNext = computed(() => this.activeStep < this.steps().length - 1);

  // Getter for template iteration
  get visibleStepsArray() {
    return this.visibleSteps();
  }

  // Getters for template access
  get visibleStepStart() {
    return this._visibleStepStart();
  }
  get activeStep() {
    return this._activeStep();
  }

  // Getter to provide the display label for the selected status
  get statusLabel() {
    const status = this.statusListWithApproval().find(
      (s) => s.value === this.selectedStatus()
    );
    return status ? status.label : 'Not Selected';
  }

  // Getter to provide the color for the selected status
  get statusColor() {
    const status = this.statusList().find(
      (s) => s.value === this.selectedStatus()
    );
    return status ? status.color : '#979BA8'; // Default color if no status selected
  }

  // Modal methods
  openAssignModal = () => {
    // Prevent opening modal for parent tasks with children
    if (this.childExist()) {
      return;
    }
    this.showAssignModal.set(true);
  };

  closeAssignModal = () => {
    this.showAssignModal.set(false);
  };

  openApprovalsModal = () => {
    this.showApprovalsModal.set(true);
    if (!this.singleApprovalData()) {
      this.selectedApproval.set(null);
    }
  };

  closeApprovalsModal = () => {
    this.showApprovalsModal.set(false);
    this.selectedStatusApproval.set(null);
    this.showApprovalValidationErrors.set(false);
    if (!this.singleApprovalData()) {
      this.selectedApproval.set(null);
    }
  };

  // Validation method for approvals - pure function (no side effects)
  validateApprovalData(approval: any | null): { isValid: boolean; errors: string[]; invalidFields: Set<string> } {
    const errors: string[] = [];
    const invalidFieldsSet = new Set<string>();
    
    if (!approval || !approval.approvalPaths || approval.approvalPaths.length === 0) {
      errors.push('At least one approval path is required');
      return { isValid: false, errors, invalidFields: invalidFieldsSet };
    }

    approval.approvalPaths.forEach((path: any, pathIndex: number) => {
      // Validate path start status
      if (!path.pathStartStatus || (typeof path.pathStartStatus === 'string' && path.pathStartStatus.trim() === '')) {
        errors.push(`Path ${path.path}: Start status is required`);
        invalidFieldsSet.add(`path-${pathIndex}-startStatus`);
      }

      // Validate reject status (can be 'previousStatus' or a valid status ID)
      if (!path.rejectStatus || (path.rejectStatus !== 'previousStatus' && typeof path.rejectStatus === 'string' && path.rejectStatus.trim() === '')) {
        errors.push(`Path ${path.path}: Rejection status is required`);
        invalidFieldsSet.add(`path-${pathIndex}-rejectStatus`);
      }

      // Validate stages
      if (!path.stages || path.stages.length === 0) {
        errors.push(`Path ${path.path}: At least one stage is required`);
      } else {
        path.stages.forEach((stage: any, stageIndex: number) => {
          // Validate stage name
          if (!stage.stageName || stage.stageName.trim() === '' || stage.stageName.trim() === 'New Stage') {
            errors.push(`Path ${path.path}, Stage ${stage.stageNumber}: Stage name is required`);
            invalidFieldsSet.add(`path-${pathIndex}-stage-${stageIndex}-name`);
          }

          // Validate approvers
          if (!stage.approvers || stage.approvers.length === 0) {
            errors.push(`Path ${path.path}, Stage ${stage.stageNumber}: At least one approver is required`);
            invalidFieldsSet.add(`path-${pathIndex}-stage-${stageIndex}-approvers`);
          }
        });
      }
    });

    return { isValid: errors.length === 0, errors, invalidFields: invalidFieldsSet };
  }

  // Computed signal for approval validation results
  approvalValidationResult = computed(() => {
    // Include trigger in dependencies to force recomputation when approval data is mutated
    this.approvalDataChangeTrigger();
    const approval = this.getSelectedApprovalDetails();
    return this.validateApprovalData(approval);
  });

  // Computed signal for approval invalid fields - only computed when validation errors are shown
  approvalInvalidFieldsComputed = computed(() => {
    if (!this.showApprovalValidationErrors()) {
      return new Set<string>();
    }
    return this.approvalValidationResult().invalidFields;
  });

  // Computed signal for approval validation errors - only computed when validation errors are shown
  approvalValidationErrorsComputed = computed(() => {
    if (!this.showApprovalValidationErrors()) {
      return [];
    }
    return this.approvalValidationResult().errors;
  });

  // Helper methods to check if specific fields are invalid
  isApprovalPathStartStatusInvalid(pathIndex: number): boolean {
    return this.showApprovalValidationErrors() && this.approvalInvalidFieldsComputed().has(`path-${pathIndex}-startStatus`);
  }

  isApprovalPathRejectStatusInvalid(pathIndex: number): boolean {
    return this.showApprovalValidationErrors() && this.approvalInvalidFieldsComputed().has(`path-${pathIndex}-rejectStatus`);
  }

  isApprovalStageNameInvalid(pathIndex: number, stageIndex: number): boolean {
    return this.showApprovalValidationErrors() && this.approvalInvalidFieldsComputed().has(`path-${pathIndex}-stage-${stageIndex}-name`);
  }

  isApprovalStageApproversInvalid(pathIndex: number, stageIndex: number): boolean {
    return this.showApprovalValidationErrors() && this.approvalInvalidFieldsComputed().has(`path-${pathIndex}-stage-${stageIndex}-approvers`);
  }

  // Computed signal for approval form validity - only disable button when validation errors are shown
  isApprovalFormValid = computed(() => {
    if (!this.showApprovalValidationErrors()) {
      return true; // Enable button by default
    }
    return this.approvalValidationResult().isValid;
  });

  saveApprovals = () => {
    const approval = this.getSelectedApprovalDetails();
    if (!approval) {
      this.toastr.error('No approval selected for saving');
      return;
    }

    // Validate before saving - use computed result
    const validation = this.approvalValidationResult();
    if (!validation.isValid) {
      this.showApprovalValidationErrors.set(true);
      return;
    }

    this.showApprovalValidationErrors.set(false);

    const currentObjID = this.itemId();

    // Determine if this is an update or create operation
    const isUpdate = this.singleApprovalData() !== null;

    // Prepare request body according to the specified structure
    const requestBody = isUpdate
      ? {
          approvalID: approval._id, // For updates, use approvalID instead of masterID
          objCode: this.getItemIdFieldCode(), // Current object code (ISSUE, PROJECT, etc.)
          objID: currentObjID,
          approvalName: approval.approvalName,
          approvalDescription: approval.approvalDescription || '',
          approvalPaths: approval.approvalPaths.map((path: any) => ({
            path: path.path,
            pathStartStatus: path.pathStartStatus,
            rejectStatus: path.rejectStatus,
            isApproved: path.isApproved,
            stages: path.stages.map((stage: any) => ({
              stageNumber: stage.stageNumber,
              stageName: stage.stageName,
              approvers: stage.approvers.map((approver: any) => ({
                type: approver.type || '',
                name: approver.name,
                _id: approver._id,
                status: approver.status || '',
              })),
              isOneDecision: stage.isOneDecision || false,
              approvalCondition: stage.approvalCondition || '',
              status: stage.status || '',
              approvedBy: stage.approvedBy || [],
              rejectedBy: stage.rejectedBy || [],
            })),
          })),
          currentStage: approval.currentStage || 1,
          currentPath: approval.currentPath || 1,
          status: approval.status || '',
        }
      : {
          masterID: approval._id, // For creates, use masterID
          objCode: this.getItemIdFieldCode(), // Current object code (ISSUE, PROJECT, etc.)
          objID: currentObjID, // Current object ID from route
          approvalName: approval.approvalName,
          approvalDescription: approval.approvalDescription || '',
          approvalPaths: approval.approvalPaths.map((path: any) => ({
            path: path.path,
            pathStartStatus: path.pathStartStatus,
            rejectStatus: path.rejectStatus,
            isApproved: path.isApproved,
            stages: path.stages.map((stage: any) => ({
              stageNumber: stage.stageNumber,
              stageName: stage.stageName,
              approvers: stage.approvers.map((approver: any) => ({
                type: approver.type || '',
                name: approver.name,
                _id: approver._id,
                status: approver.status || '',
              })),
              isOneDecision: stage.isOneDecision || false,
              approvalCondition: stage.approvalCondition || '',
              status: stage.status || '',
              approvedBy: stage.approvedBy || [],
              rejectedBy: stage.rejectedBy || [],
            })),
          })),
          currentStage: approval.currentStage || 1,
          currentPath: approval.currentPath || 1,
          status: approval.status || '',
        };

    this.isSavingApproval.set(true);
    if (isUpdate) {
      // Call the updateNewApproval API for existing approvals
      this.approvalService.updateNewApproval(requestBody).subscribe({
        next: (res: any) => {
          this.toastr.success(res.message || 'Approval updated successfully');
          this.closeApprovalsModal();
          this.approvalExists.set(true);
          this.getApprovalTracking(this.itemId());
          // Trigger refresh for approvals component
          this.approvalRefreshService.triggerRefresh();
          this.isSavingApproval.set(false);
        },
        error: (err: any) => {
          console.error('Error updating approval:', err);
          this.toastr.error(err?.message || 'Error updating approval');
          this.isSavingApproval.set(false);
        },
      });
    } else {
      // Call the createNewApproval API for new approvals
      this.approvalService.createNewApproval(requestBody).subscribe({
        next: (res: any) => {
          this.toastr.success(res?.message || 'Approval saved successfully');
          this.closeApprovalsModal();
          // Clear cache before refreshing to ensure fresh data with new approval
          const id = this.itemId();
          if (id) {
            if (this.service().clearSingleTaskCache) {
              this.service().clearSingleTaskCache(id);
            }
            if (this.service().clearSingleIssueCache) {
              this.service().clearSingleIssueCache(id);
            }
            if (this.service().clearSingleProjectCache) {
              this.service().clearSingleProjectCache(id);
            }
            if (this.service().clearSingleRevisionCache) {
              this.service().clearSingleRevisionCache(id);
            }
            if (this.service().clearSingleRequestCache) {
              this.service().clearSingleRequestCache(id);
            }
          }
          // Refresh item details first to get the updated approvalProcess ID
          this.getItemDetails(this.itemId());
          if (res?.success && res?.data) {
            // Set the approval data from response
            this.singleApprovalData.set(res.data);
            this.selectedApproval.set(res.data._id);
            this.approvalExists.set(true);
          }
          // Trigger refresh for approvals component
          this.approvalRefreshService.triggerRefresh();
          this.isSavingApproval.set(false);
        },
        error: (err: any) => {
          this.toastr.error(err?.message || 'Error saving approval');
          this.isSavingApproval.set(false);
        },
      });
    }
  };

  // Handle assign users from shared component
  onAssignUsers = (event: {
    newlySelectedUsers: AssigneeUser[];
    removedUsers: AssigneeUser[];
  }) => {
    const { newlySelectedUsers, removedUsers } = event;

    if (newlySelectedUsers.length === 0 && removedUsers.length === 0) {
      return;
    }

    const uniqueAssignedUsersMap = new Map<string, AssigneeUser>();
    this.assignedUsers().forEach((user) => {
      uniqueAssignedUsersMap.set(user._id, user);
    });

    // Add newly selected users
    newlySelectedUsers.forEach((user) => {
      uniqueAssignedUsersMap.set(user._id, user);
    });

    // Remove users that were removed
    removedUsers.forEach((user) => {
      uniqueAssignedUsersMap.delete(user._id);
    });

    this.assignedUsers.set(Array.from(uniqueAssignedUsersMap.values()));

    if (this.itemId()) {
      const params: Record<string, any> = {
        [this.getItemIdField()]: this.itemId(),
      };
      if (newlySelectedUsers.length > 0) {
        params['assignees'] = newlySelectedUsers;
      }
      if (removedUsers.length > 0) {
        params['removedAssignees'] = removedUsers;
      }
      this.updateItem(params);
    }
  };

  // Handler to open remove confirmation popup
  openRemoveConfirmPopup = (user: AssigneeUser) => {
    // Prevent removing users for parent tasks with children
    if (this.childExist()) {
      return;
    }
    this.userToRemove.set(user);
    this.showRemoveConfirmPopup.set(true);
  };

  // Handler to confirm removal
  confirmRemoveAssignedUser = () => {
    const user = this.userToRemove();
    if (user) {
      this.removeAssignedUser(user);
    }
    this.showRemoveConfirmPopup.set(false);
    this.userToRemove.set(null);
  };

  // Handler to cancel removal
  cancelRemoveAssignedUser = () => {
    this.showRemoveConfirmPopup.set(false);
    this.userToRemove.set(null);
  };

  // Handler to open delete approval confirmation popup
  openDeleteApprovalConfirmPopup = () => {
    this.approvalToDelete.set(this.singleApprovalData());
    this.showDeleteApprovalConfirmPopup.set(true);
  };

  // Handler to confirm approval deletion
  confirmDeleteApproval = () => {
    const approval = this.approvalToDelete();
    if (approval && approval._id) {
      this.deleteApproval(approval._id);
      // Don't close popup here - let deleteApproval handle it after API call completes
    } else {
      this.showDeleteApprovalConfirmPopup.set(false);
      this.approvalToDelete.set(null);
    }
  };

  // Handler to cancel approval deletion
  cancelDeleteApproval = () => {
    this.showDeleteApprovalConfirmPopup.set(false);
    this.approvalToDelete.set(null);
  };

  // Change removeAssignedUser to only do the removal (no popup logic)
  removeAssignedUser = (user: AssigneeUser) => {
    this.assignedUsers.set(
      this.assignedUsers().filter((u) => u._id !== user._id)
    );
    this.updateItem({
      [this.getItemIdField()]: this.itemId(),
      removedAssignees: [user],
    });
  };

  getDisplayName = (item: any): string => {
    if (!item) return 'Unknown';
    return (
      item.name ||
      item.jobrole ||
      item.teamName ||
      item.jobroles?.jobrole ||
      'Unknown'
    );
  };

  @HostListener('document:mousedown', ['$event'])
  onDocumentMouseDown(event: MouseEvent) {
    // Store mouse down position for drag detection
    this.mouseDownPosition.set({ x: event.clientX, y: event.clientY });
    this.isDragging.set(false);
  }

  @HostListener('document:mousemove', ['$event'])
  onDocumentMouseMove(event: MouseEvent) {
    // Detect if user is dragging (mouse moved more than 5px from initial position)
    if (this.mouseDownPosition()) {
      const deltaX = Math.abs(event.clientX - this.mouseDownPosition()!.x);
      const deltaY = Math.abs(event.clientY - this.mouseDownPosition()!.y);

      if (deltaX > 5 || deltaY > 5) {
        this.isDragging.set(true);
      }
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    // Planned hours input click-outside logic - only close if clicked outside and not dragging
    if (this.showInput() && this.inputBox && !this.isDragging()) {
      const target = event.target as HTMLElement;
      const inputWrapper = target.closest('.input_wrap');
      const isClickInsideInput = this.inputBox.nativeElement.contains(
        event.target
      );
      const isClickInsideWrapper = inputWrapper !== null;

      // Only close if clicked outside both the input and its wrapper, and not dragging
      if (!isClickInsideInput && !isClickInsideWrapper) {
        this.submittedValue.set(this.inputValue() || this.submittedValue());
        this.showInput.set(false);
        this.onPlannedHoursChange();
      }
    }

    // Reset drag state after click
    this.isDragging.set(false);
    this.mouseDownPosition.set(null);
  }

  copyRefNo = (refNo: string | null) => {
    if (refNo) {
      navigator.clipboard.writeText(refNo.trim());
      this.showDivCopy.set(true);
      setTimeout(() => {
        this.showDivCopy.set(false);
      }, 2000);
    }
  };

  onClickInside = (event: MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
  };

  // Input-specific event handlers to prevent unwanted closures during text selection
  onInputMouseDown = (event: MouseEvent) => {
    // Prevent drag detection when clicking inside input
    this.isDragging.set(false);
    event.stopPropagation();
  };

  onInputMouseUp = (event: MouseEvent) => {
    // Prevent drag detection when releasing mouse inside input
    this.isDragging.set(false);
    event.stopPropagation();
  };

  onInputSelectStart = (event: Event) => {
    // Prevent drag detection when text selection starts
    this.isDragging.set(false);
    event.stopPropagation();
  };

  // Handle keydown events specifically for Escape key
  onInputKeyDown = (event: KeyboardEvent) => {
    // Handle Escape key - cancel editing without updating value
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      // Reset to original value and close input
      this.inputValue.set(this.originalPlannedHours());
      this.showInput.set(false);
      // Reset loading state if it was active
      this.isUpdatingPlannedHours.set(false);
      return;
    }
  };

  enableInput = () => {
    this.showInput.set(true);
    this.originalPlannedHours.set(this.submittedValue());
    this.inputValue.set(this.submittedValue());
    // Use requestAnimationFrame for better timing
    requestAnimationFrame(() => {
      if (this.inputBox?.nativeElement) {
        this.inputBox.nativeElement.focus();
        // Select all text for easy editing
        this.inputBox.nativeElement.select();
      }
    });
  };

  allowOnlyDecimalNumbers = (event: KeyboardEvent) => {
    const inputChar = event.key;
    const currentValue = this.inputValue() || '';

    // Handle Enter key - save and close input
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      this.submittedValue.set(this.inputValue() || this.submittedValue());
      this.showInput.set(false);
      this.onPlannedHoursChange();
      return;
    }

    // Allow only decimal numbers
    if (!/[\d.]/.test(inputChar)) {
      event.preventDefault();
      return;
    }

    // Prevent multiple decimal points
    if (inputChar === '.' && currentValue.includes('.')) {
      event.preventDefault();
      return;
    }

    // Limit length to 5 characters (unless text is selected)
    if (currentValue.length >= 5 && !this.isTextSelected()) {
      event.preventDefault();
      return;
    }
  };

  isTextSelected = (): boolean => {
    const input = this.inputBox.nativeElement;
    return input.selectionStart !== input.selectionEnd;
  };

  // Date filter function to prevent past dates based on config type
  dateFilter = (date: Date | null): boolean => {
    if (!date) return false;

    const config = this.config();
    let minDate: Date;

    if (config.type === 'task') {
      // For tasks, disable dates before the start date or today (whichever is later)
      const startDate = this.itemDetails()?.plannedStartDate;
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (startDate) {
        const taskStartDate = new Date(startDate);
        taskStartDate.setHours(0, 0, 0, 0);
        // Use the later of start date or today
        minDate = taskStartDate > today ? taskStartDate : today;
      } else {
        // Fallback to today if no start date is available
        minDate = today;
      }
    } else {
      // For issues, revisions, projects, requests, etc., disable dates before today
      minDate = new Date();
      minDate.setHours(0, 0, 0, 0);
    }

    return date >= minDate;
  };

  openPicker = () => {
    this.picker.open();
  };

  onDateChange = (event: any) => {
    this.selectedDate.set(event.value);
    if (this.itemId() && this.selectedDate()) {
      const params = {
        [this.getItemIdField()]: this.itemId(),
        projectedCompletionDate: this.selectedDate(),
        plannedCompletionDate: this.selectedDate(),
      };
      this.updateItem(params);
    }
  };

  toggleDiv = () => {
    this.showDiv.set(!this.showDiv());
  };

  prevStep = () => {
    if (this.activeStep > 0) {
      this._activeStep.set(this.activeStep - 1);
      this.updateVisibleSteps();
    }
  };

  nextStep = () => {
    if (this.activeStep < this.steps().length - 1) {
      this._activeStep.set(this.activeStep + 1);
      this.updateVisibleSteps();
    }
  };

  updateVisibleSteps = () => {
    const steps = this.steps();
    if (steps.length <= 3) {
      this._visibleStepStart.set(0);
      return;
    }
    if (this.activeStep <= 1) {
      this._visibleStepStart.set(0);
    } else if (this.activeStep >= steps.length - 2) {
      this._visibleStepStart.set(steps.length - 3);
    } else {
      this._visibleStepStart.set(this.activeStep - 1);
    }
  };

  // Helper method to get the correct ID field based on config type
  getItemIdField(): string {
    const config = this.config();
    switch (config.type) {
      case 'task':
        return 'taskID';
      case 'issue':
        return 'issueID';
      case 'project':
        return 'projectID';
      case 'revision':
        return 'revisionID';
      case 'request':
        return 'requestID';
      default:
        return 'itemID';
    }
  }

  getItemIdFieldCode(): string {
    const config = this.config();
    switch (config.type) {
      case 'task':
        return 'TASK';
      case 'issue':
        return 'ISSUE';
      case 'project':
        return 'PROJECT';
      case 'revision':
        return 'REVISION';
      case 'request':
        return 'REQUEST';
      default:
        return 'ITEM';
    }
  }

  // Helper method to get the correct status field based on config type
  getStatusField(): string {
    const config = this.config();
    switch (config.type) {
      case 'task':
        return 'taskStatus';
      case 'issue':
        return 'issueStatus';
      case 'project':
        return 'projectStatus';
      case 'revision':
        return 'revisionStatus';
      case 'request':
        return 'requestStatus';
      default:
        return 'status';
    }
  }

  getItemDetails(itemId: any) {
    const service = this.service();
    const method = this.getDetailsMethod();

    service[method](itemId).subscribe({
      next: (res: any) => {
        this.itemDetails.set(res?.data);
        this.assignedUsers.set(
          (res?.data?.assignees || []).map((assignee: any) => ({
            _id: assignee._id,
            name:
              assignee.name ||
              assignee.jobrole ||
              assignee.teamName ||
              'Unknown',
            image:
              assignee.image || assignee.avatar || 'assets/images/avatar.png',
            assignType: assignee.assignType,
          }))
        );
        this.selectedDate.set(
          res?.data?.projectedCompletionDate
            ? new Date(res?.data?.projectedCompletionDate)
            : new Date(res?.data?.plannedCompletionDate)
        );
        this.submittedValue.set(res?.data?.plannedHours);
        this.originalPlannedHours.set(res?.data?.plannedHours);
        this.childExist.set(res?.data?.childExist);
        this.selectedStatus.set(
          res?.data?.taskStatusData?._id ||
            res?.data?.issueStatusData?._id ||
            res?.data?.projectStatusData?._id ||
            res?.data?.revisionStatusData?._id ||
            res?.data?.requestStatusData?._id
        );
        this.selectedApproval.set(res?.data?.approvalProcess);
        this.selectedStatusValue.set(
          res?.data?.taskStatusData?.statusName ||
            res?.data?.issueStatusData?.statusName ||
            res?.data?.projectStatusData?.statusName ||
            res?.data?.revisionStatusData?.statusName ||
            res?.data?.requestStatusData?.statusName ||
            null
        );
        this.selectedStatusApproval.set(res?.data?.statusApproval);
        this.updateStatusListApproval();
        this.getApprovalSetStatus();

        // Calculate task percentage for time summary
        this.calculateTaskPercentage(res?.data);

        // Call getSingleApproval if approvalProcess exists
        if (res?.data?.approvalProcess) {
          this.getSingleApproval(res.data.approvalProcess);
        } else {
          // If no approvalProcess, set approvalExists to false
          this.approvalExists.set(false);
          this.singleApprovalData.set(null);
        }
      },
      error: (err: any) => {
        console.log(err);
      },
    });
  }

  getAssignList() {
    this.isLoadingData.set(true);
    this.apiService.getAssignList().subscribe({
      next: (res: any) => {
        this.allAssignableData.set(
          (res?.data || []).map((item: any) => {
            const normalizedItem = {
              ...item,
              name: item.name || item.jobrole || item.teamName || 'Unknown',
              image: item.image || item.avatar || 'assets/images/avatar.png',
            };
            return normalizedItem;
          })
        );
        this.isLoadingData.set(false);
      },
      error: (err: any) => {
        console.log(err);
        this.isLoadingData.set(false);
      },
    });
  }

  getStatus() {
    const config = this.config();
    const objCode = config.type.toUpperCase();

    this.apiService.getStatus({ objCode }).subscribe({
      next: (res: any) => {
        this.statusList.set(
          res?.data?.map((item: any) => ({
            label: item.statusName,
            value: item._id,
            color: item.color,
          }))
        );
        this.updateStatusListApproval();
      },
      error: (err: any) => {
        console.log(err);
      },
    });
  }

  getApprovalSetStatus() {
    this.apiService
      .getApprovalExistingStatuses(this.itemId(), this.getItemIdFieldCode())
      .subscribe({
        next: (res: any) => {
          this.pathStartStatusList.set(res?.data?.pathStartStatusList || []);
        },
        error: (err: any) => {
          console.log(err);
        },
      });
  }

  updateStatusListApproval = () => {
    this.statusListApproval.set(
      this.statusList().filter(
        (item: any) => item.value !== this.selectedStatus()
      )
    );
  };

  // Computed signal for status list with 'Pending Approval' label
  statusListWithApproval = computed(() => {
    const pendingList = this.pathStartStatusList();
    return this.statusList().map((item) => ({
      ...item,
      label: pendingList.includes(item.value)
        ? `${item.label} - Pending Approval`
        : item.label,
    }));
  });

  getApprovalTracking(itemId: any) {
    this.projectsService
      .getApprovalTracking(itemId, this.getItemIdFieldCode())
      .subscribe({
        next: (res: any) => {
          this.userPreference.set(res?.data?.userPreference);
          this.approvalPaths.set(res?.data?.approvalPaths[0] || []);

          if(this.approvalPaths()?.stages?.length > 0) {
            const isRequestedStage = this.approvalPaths()?.stages.find((stage: any) => stage.status === 'requested');
            if(isRequestedStage) {
              const userID = localStorage.getItem('userID');
              const jobRoleID = localStorage.getItem('jobRoleID');
              const homeTeamID = localStorage.getItem('homeTeamID');
              const approvers = isRequestedStage.approvers.find((approver: any) => approver.status === 'requested' && (approver._id === userID || approver._id === jobRoleID || approver._id === homeTeamID));
              if(approvers) {
                this.approverID.set(approvers._id);
                this.displayApprovalRejectButton.set(true);
              }
            }
          }
          this.steps.set(
            this.approvalPaths()?.stages?.map((item: any) => ({
              label: item.stageName,
              status: item.status,
            })) || []
          );
          let lastApprovedIndex = -1;
          for (let i = 0; i < this.steps()?.length; i++) {
            if (this.steps()[i]?.status === 'approved') {
              lastApprovedIndex = i;
            }
          }
          this._activeStep.set(
            lastApprovedIndex !== -1 ? lastApprovedIndex + 1 : 0
          );
          if (this.activeStep >= this.steps()?.length) {
            this._activeStep.set(this.steps()?.length - 1);
          }
          this.updateVisibleSteps();
        },
        error: (err: any) => {
          console.log(err);
        },
      });
  }

  approveApproval() {
    this.isApprovingApproval.set(true);
    this.approvalService.acceptOrRejectApproval({ 
      approvalProcessID: this.selectedApproval(),
      approverID: this.approverID(),
      action: 'approve',
      objCode: this.getItemIdFieldCode(),
      objID: this.itemId(),
    }).subscribe({
      next: (res: any) => {
        this.toastr.success(res?.message || 'Approval approved successfully');
        // Refresh approval tracking and trigger refresh for approvals component
        this.getApprovalTracking(this.itemId());
        this.approvalRefreshService.triggerRefresh();
        this.isApprovingApproval.set(false);
        this.displayApprovalRejectButton.set(false);
      },
      error: (err: any) => {
        this.isApprovingApproval.set(false);
      },
    });
  }

  rejectApproval() {
    this.isRejectingApproval.set(true);
    this.approvalService.acceptOrRejectApproval({
      approvalProcessID: this.selectedApproval(),
      approverID: this.approverID(),
      action: 'reject',
      objCode: this.getItemIdFieldCode(),
      objID: this.itemId(),
    }).subscribe({
      next: (res: any) => {
        this.toastr.success(res?.message || 'Approval rejected successfully');
        // Refresh approval tracking and trigger refresh for approvals component
        this.getApprovalTracking(this.itemId());
        this.approvalRefreshService.triggerRefresh();
        this.isRejectingApproval.set(false);
        this.displayApprovalRejectButton.set(false);
      },
      error: (err: any) => {
        this.isRejectingApproval.set(false);
      },
    });
  }

  getApprovals() {
    const config = this.config();
    const objCode = config.type.toUpperCase();

    this.approvalService.getApprovals({ objCode }).subscribe({
      next: (res) => {
        this.approvalList.set(
          res?.data?.map((item: any) => ({
            _id: item._id,
            name: item.approvalName,
            approvalName: item.approvalName,
            approvalDescription: item.approvalDescription,
            approvalPaths: item.approvalPaths || [],
            currentStage: item.currentStage,
            currentPath: item.currentPath,
            status: item.status,
            createdAt: item.createdAt,
            updatedAt: item.updatedAt,
            objCode: item.objCode,
          }))
        );
      },
      error: (err) => {
        console.error('Error loading approvals:', err);
      },
    });
  }

  getSingleApproval(approvalProcessId: string) {
    this.approvalService.getSingleApproval(approvalProcessId).subscribe({
      next: (res: any) => {
        if (res?.success && res?.data) {
          this.singleApprovalData.set(res.data);
          // Set the selected approval to the single approval data
          this.selectedApproval.set(res.data._id);
          this.approvalExists.set(true);
        } else {
          // If success is false or no data, approval doesn't exist
          this.approvalExists.set(false);
          this.singleApprovalData.set(null);
          this.selectedApproval.set(null);
        }
      },
      error: (err: any) => {
        console.error('Error loading single approval:', err);
        // On error, assume approval doesn't exist
        this.approvalExists.set(false);
        this.singleApprovalData.set(null);
        this.selectedApproval.set(null);
      },
    });
  }

  // Load assign list for approver selection
  loadAssignList() {
    this.apiService.getAssignList().subscribe({
      next: (res: any) => {
        this.assignList.set(res?.data || []);
      },
      error: (err: any) => {
        console.error('Error loading assign list:', err);
        this.assignList.set([]);
      },
    });
  }

  // Get selected approval details
  getSelectedApprovalDetails(): any | null {
    if (!this.selectedApproval()) return null;

    // First check if we have single approval data (for existing approvals)
    if (this.singleApprovalData()) {
      return this.singleApprovalData();
    }

    // Otherwise, check the approval list (for new approvals)
    const approval = this.approvalList().find(
      (approval) => approval._id === this.selectedApproval()
    );
    return approval || null;
  }

  // Stage display status helper
  getStageDisplayStatus(status: string | undefined): string {
    if (!status) return 'Not Started';

    switch (status.toLowerCase()) {
      case 'pending':
        return 'requested';
      case 'approved':
        return 'approved';
      case 'rejected':
        return 'rejected';
      case 'completed':
        return 'completed';
      default:
        return 'Not Started';
    }
  }

  // Path management methods
  updatePathStartStatus(pathIndex: number, newStatus: string) {
    const approval = this.getSelectedApprovalDetails();
    if (
      approval &&
      approval.approvalPaths &&
      approval.approvalPaths[pathIndex]
    ) {
      approval.approvalPaths[pathIndex].pathStartStatus = newStatus;
      // Trigger recomputation of validation
      this.approvalDataChangeTrigger.update(v => v + 1);
      // Validation is now handled by computed signals - check if we should hide errors
      if (this.showApprovalValidationErrors()) {
        const validation = this.approvalValidationResult();
        if (validation.errors.length === 0) {
          this.showApprovalValidationErrors.set(false);
        }
      }
    }
  }

  updatePathRejectStatus(pathIndex: number, newStatus: string) {
    const approval = this.getSelectedApprovalDetails();
    if (
      approval &&
      approval.approvalPaths &&
      approval.approvalPaths[pathIndex]
    ) {
      // If user clears the selection, set to 'previousStatus' as default
      approval.approvalPaths[pathIndex].rejectStatus = newStatus || 'previousStatus';
      // Trigger recomputation of validation
      this.approvalDataChangeTrigger.update(v => v + 1);
      // Validation is now handled by computed signals - check if we should hide errors
      if (this.showApprovalValidationErrors()) {
        const validation = this.approvalValidationResult();
        if (validation.errors.length === 0) {
          this.showApprovalValidationErrors.set(false);
        }
      }
    }
  }

  addNewPath() {
    const approval = this.getSelectedApprovalDetails();
    if (approval && approval.approvalPaths) {
      const newPathNumber = approval.approvalPaths.length + 1;
      const newPath = {
        path: newPathNumber,
        pathStartStatus: null,
        rejectStatus: 'previousStatus',
        isApproved: false,
        stages: [
          {
            stageNumber: 1,
            stageName: 'New Stage',
            approvers: [],
            isOneDecision: false,
            status: 'pending',
            approvedBy: [],
            rejectedBy: [],
          },
        ],
      };
      approval.approvalPaths.push(newPath);
      // Trigger recomputation of validation
      this.approvalDataChangeTrigger.update(v => v + 1);
    }
  }

  removePath(pathIndex: number) {
    const approval = this.getSelectedApprovalDetails();
    if (
      approval &&
      approval.approvalPaths &&
      approval.approvalPaths.length > 1
    ) {
      approval.approvalPaths.splice(pathIndex, 1);
      // Renumber paths
      approval.approvalPaths.forEach((path: any, index: number) => {
        path.path = index + 1;
      });
      // Trigger recomputation of validation
      this.approvalDataChangeTrigger.update(v => v + 1);
    }
  }

  // Stage management methods
  updateStageName(pathIndex: number, stageIndex: number, newName: string) {
    const approval = this.getSelectedApprovalDetails();
    if (
      approval &&
      approval.approvalPaths &&
      approval.approvalPaths[pathIndex] &&
      approval.approvalPaths[pathIndex].stages &&
      approval.approvalPaths[pathIndex].stages[stageIndex]
    ) {
      approval.approvalPaths[pathIndex].stages[stageIndex].stageName = newName;
      // Trigger recomputation of validation
      this.approvalDataChangeTrigger.update(v => v + 1);
      // Validation is now handled by computed signals - check if we should hide errors
      if (this.showApprovalValidationErrors()) {
        const validation = this.approvalValidationResult();
        if (validation.errors.length === 0) {
          this.showApprovalValidationErrors.set(false);
        }
      }
    }
  }

  updateStageOneDecision(pathIndex: number, stageIndex: number, event: any) {
    const approval = this.getSelectedApprovalDetails();
    if (
      approval &&
      approval.approvalPaths &&
      approval.approvalPaths[pathIndex] &&
      approval.approvalPaths[pathIndex].stages &&
      approval.approvalPaths[pathIndex].stages[stageIndex]
    ) {
      approval.approvalPaths[pathIndex].stages[stageIndex].isOneDecision =
        event.target.checked;
    }
  }

  addNewStage(pathIndex: number) {
    const approval = this.getSelectedApprovalDetails();
    if (
      approval &&
      approval.approvalPaths &&
      approval.approvalPaths[pathIndex]
    ) {
      const path = approval.approvalPaths[pathIndex];
      const newStageNumber = path.stages.length + 1;
      const newStage = {
        stageNumber: newStageNumber,
        stageName: 'New Stage',
        approvers: [],
        isOneDecision: false,
        status: 'pending',
        approvedBy: [],
        rejectedBy: [],
      };
      path.stages.push(newStage);
      // Trigger recomputation of validation
      this.approvalDataChangeTrigger.update(v => v + 1);
    }
  }

  removeStage(pathIndex: number, stageIndex: number) {
    const approval = this.getSelectedApprovalDetails();
    if (
      approval &&
      approval.approvalPaths &&
      approval.approvalPaths[pathIndex] &&
      approval.approvalPaths[pathIndex].stages &&
      approval.approvalPaths[pathIndex].stages.length > 1
    ) {
      approval.approvalPaths[pathIndex].stages.splice(stageIndex, 1);
      // Renumber stages
      approval.approvalPaths[pathIndex].stages.forEach(
        (stage: any, index: number) => {
          stage.stageNumber = index + 1;
        }
      );
      // Trigger recomputation of validation
      this.approvalDataChangeTrigger.update(v => v + 1);
    }
  }

  // Approver management methods
  addApprover(
    pathIndex: number,
    stageIndex: number,
    assigneeId: string,
    selectRef?: any
  ) {
    const approval = this.getSelectedApprovalDetails();
    if (
      approval &&
      approval.approvalPaths &&
      approval.approvalPaths[pathIndex] &&
      approval.approvalPaths[pathIndex].stages &&
      approval.approvalPaths[pathIndex].stages[stageIndex]
    ) {
      const assignee = this.assignList().find(
        (item) => item._id === assigneeId
      );
      if (assignee) {
        const newApprover = {
          name: assignee.name,
          _id: assignee._id,
          type: 'PEOPLE',
        };
        approval.approvalPaths[pathIndex].stages[stageIndex].approvers.push(
          newApprover
        );
        // Trigger recomputation of validation
        this.approvalDataChangeTrigger.update(v => v + 1);
        // Validation is now handled by computed signals - check if we should hide errors
        if (this.showApprovalValidationErrors()) {
          const validation = this.approvalValidationResult();
          if (validation.errors.length === 0) {
            this.showApprovalValidationErrors.set(false);
          }
        }

        // Clear the dropdown after selection
        if (selectRef) {
          selectRef.clearModel();
        }
      }
    }
  }

  removeApprover(pathIndex: number, stageIndex: number, approverIndex: number) {
    const approval = this.getSelectedApprovalDetails();
    if (
      approval &&
      approval.approvalPaths &&
      approval.approvalPaths[pathIndex] &&
      approval.approvalPaths[pathIndex].stages &&
      approval.approvalPaths[pathIndex].stages[stageIndex]
    ) {
      approval.approvalPaths[pathIndex].stages[stageIndex].approvers.splice(
        approverIndex,
        1
      );
      // Trigger recomputation of validation
      this.approvalDataChangeTrigger.update(v => v + 1);
    }
  }

  updateItem(params: any) {
    const service = this.service();
    const method = this.updateMethod();
    const isAssigneeUpdate = params.assignees || params.removedAssignees;
    service[method](params).subscribe({
      next: (res: any) => {
        this.toastr.success(res?.message);
        // Clear the cache for this item so that getItemDetails fetches fresh data
        if (service.clearSingleTaskCache) {
          service.clearSingleTaskCache(this.itemId());
        }
        if (service.clearSingleIssueCache) {
          service.clearSingleIssueCache(this.itemId());
        }
        if (service.clearSingleProjectCache) {
          service.clearSingleProjectCache(this.itemId());
        }
        if (service.clearSingleRevisionCache) {
          service.clearSingleRevisionCache(this.itemId());
        }
        if (service.clearSingleRequestCache) {
          service.clearSingleRequestCache(this.itemId());
        }
        this.getItemDetails(this.itemId());
        const isStatusUpdate = params[this.getStatusField()];
        if (isStatusUpdate) {
          this.getApprovalTracking(this.itemId());
          // Trigger refresh in task details component to update isStatusComplete immediately
          this.statusSidebarRefreshService.triggerRefresh();
          
          // Trigger cross-tab sync and project data refresh for task status updates
          const config = this.config();
          if (config.type === 'task') {
            // Try to get projectId from multiple sources:
            // 1. Response data (if update response includes task data)
            // 2. Current itemDetails (might have old data but should have projectID)
            // 3. Route params (for tasks within a project)
            const projectId = res?.data?.projectID || 
                             res?.data?.task?.projectID ||
                             res?.data?.projectId ||
                             this.itemDetails()?.projectID || 
                             this.itemDetails()?.projectId ||
                             this.route.snapshot?.parent?.params?.['id'] ||
                             this.route.snapshot?.params?.['id'];
            
            if (projectId) {
              // Send cross-tab sync event
              const syncEvent = {
                type: 'task_status_updated',
                projectId: projectId,
                taskId: this.itemId() || undefined,
                data: {
                  projectId: projectId,
                  taskId: this.itemId(),
                  status: params[this.getStatusField()],
                  timestamp: Date.now()
                },
                timestamp: Date.now()
              };
              console.log('Status-sidebar: Sending cross-tab sync event:', syncEvent);
              this.crossTabSyncService.sendSyncEvent(syncEvent);
              
              // Trigger project data refresh to update project-status charts
              this.projectsService.triggerProjectDataRefresh(projectId);
            } else {
              console.warn('Status-sidebar: Could not determine projectId for task status update. Available data:', {
                responseData: res?.data,
                itemDetails: this.itemDetails(),
                routeParams: this.route.snapshot?.params,
                parentRouteParams: this.route.snapshot?.parent?.params
              });
            }
          }
        }
        // Reset planned hours loading state if this was a planned hours update
        if (params.plannedHours !== undefined) {
          this.isUpdatingPlannedHours.set(false);
        }
        // If this was an assignee update, trigger refresh in task details component
        if (isAssigneeUpdate) {
          this.statusSidebarRefreshService.triggerRefresh();
        }
      },
      error: (err: any) => {
        this.toastr.error(err?.error?.message);
        // Reset planned hours loading state on error
        if (params.plannedHours !== undefined) {
          this.isUpdatingPlannedHours.set(false);
        }
      },
    });
  }

  // Delete approval method
  deleteApproval(approvalID: string) {
    this.isLoadingApprovals.set(true);

    const params = {
      approvalID: approvalID,
    };

    this.approvalService.deleteApproval(params).subscribe({
      next: (res: any) => {
        this.toastr.success(res?.message || 'Approval deleted successfully');
        // Close the delete confirmation popup
        this.showDeleteApprovalConfirmPopup.set(false);
        this.approvalToDelete.set(null);
        this.closeApprovalsModal();
        // Clear the single approval data since it's been deleted
        this.singleApprovalData.set(null);
        this.selectedApproval.set(null);
        this.approvalExists.set(false);
        // Immediately update itemDetails to clear approval-related fields for instant UI update
        const currentItemDetails = this.itemDetails();
        if (currentItemDetails) {
          this.itemDetails.set({
            ...currentItemDetails,
            approvalProcess: null,
            approvalName: null
          });
        }
        // Clear cache before refreshing to ensure fresh data
        const id = this.itemId();
        if (id) {
          if (this.service().clearSingleTaskCache) {
            this.service().clearSingleTaskCache(id);
          }
          if (this.service().clearSingleIssueCache) {
            this.service().clearSingleIssueCache(id);
          }
          if (this.service().clearSingleProjectCache) {
            this.service().clearSingleProjectCache(id);
          }
          if (this.service().clearSingleRevisionCache) {
            this.service().clearSingleRevisionCache(id);
          }
          if (this.service().clearSingleRequestCache) {
            this.service().clearSingleRequestCache(id);
          }
        }
        // Refresh the item details to reflect the change
        this.getItemDetails(this.itemId());
        this.getApprovalTracking(this.itemId());
        this.isLoadingApprovals.set(false);
        // Trigger refresh for approvals component
        this.approvalRefreshService.triggerRefresh();
      },
      error: (err: any) => {
        this.toastr.error(err?.message || 'Error deleting approval');
        // Close the delete confirmation popup on error too
        this.showDeleteApprovalConfirmPopup.set(false);
        this.approvalToDelete.set(null);
        this.isLoadingApprovals.set(false);
      },
    });
  }

  onStatusChange = (newStatus: string) => {
    // Prevent status change for parent tasks with children
    if (this.childExist()) {
      // Revert to original status
      const currentStatus = this.itemDetails()?.taskStatusData?._id ||
        this.itemDetails()?.issueStatusData?._id ||
        this.itemDetails()?.projectStatusData?._id ||
        this.itemDetails()?.revisionStatusData?._id ||
        this.itemDetails()?.requestStatusData?._id;
      this.selectedStatus.set(currentStatus);
      return;
    }
    this.selectedStatus.set(newStatus);
    this.updateStatusListApproval();
    if (this.itemId()) {
      const params = {
        [this.getItemIdField()]: this.itemId(),
        [this.getStatusField()]: newStatus,
      };
      this.updateItem(params);
    }
  };

  onPlannedHoursChange = () => {
    // Only update if there's a valid item ID and the value has actually changed
    if (
      this.itemId() &&
      this.submittedValue() &&
      this.submittedValue() !== this.originalPlannedHours()
    ) {
      this.isUpdatingPlannedHours.set(true);
      const params = {
        [this.getItemIdField()]: this.itemId(),
        plannedHours: parseFloat(this.submittedValue()),
      };
      this.updateItem(params);
    }
  };

  // Enhanced blur handler that's more intelligent about when to close
  onInputBlur = (event: FocusEvent) => {
    // Only close if we're not dragging and the focus is actually leaving the input area
    if (!this.isDragging()) {
      // Small delay to allow for potential focus changes within the same component
      setTimeout(() => {
        if (
          this.showInput() &&
          !this.inputBox?.nativeElement?.contains(document.activeElement)
        ) {
          this.submittedValue.set(this.inputValue() || this.submittedValue());
          this.showInput.set(false);
          this.onPlannedHoursChange();
        }
      }, 100);
    }
  };

  onApprovalChange = (approvalId: string) => {
    this.selectedApproval.set(approvalId);
  };

  onApprovalStatusChange = (newStatus: string) => {
    this.selectedStatusApproval.set(newStatus);
  };

  goBackToApprovalSelection = () => {
    this.selectedApproval.set(null);
    this.singleApprovalData.set(null);
    this.showApprovalValidationErrors.set(false);
  };

  selectTab = (tab: 'manage_item' | 'time_summary') => {
    this.selectedTab.set(tab);
    
    if (tab === 'time_summary' && this.itemId() && this.config().showTimeSummary) {
      // Destroy existing charts first
      this.destroyCharts();
      // Load data and initialize charts
      this.loadTaskTimeSummary();
    }
  };

  ngAfterViewInit(): void {
    // Add resize listener for charts
    fromEvent(window, 'resize').pipe(
      debounceTime(300),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      if (this.selectedTab() === 'time_summary') {
        this.forceChartInitialization();
      }
    });

    // Watch for changes in cached hour data and refresh time summary if tab is active
    // Use interval to check for cache updates (polling approach to avoid infinite loops)
    // Check every 1 second to balance responsiveness with performance
    interval(1000).pipe(
      takeUntil(this.destroy$),
      filter(() => {
        // Only check when time summary tab is active
        const shouldCheck = this.selectedTab() === 'time_summary' && 
                           this.itemId() !== null && 
                           this.config().showTimeSummary &&
                           !this.isProcessingCacheUpdate;
        return shouldCheck === true;
      })
    ).subscribe(() => {
      this.checkAndUpdateFromCache();
    });
  }

  /**
   * Check for cached hour data updates and refresh if available
   */
  private checkAndUpdateFromCache(): void {
    if (this.isProcessingCacheUpdate) {
      return;
    }

    const itemId = this.itemId();
    const config = this.config();
    
    if (!itemId || !config.showTimeSummary) {
      return;
    }

    const cachedHours = this.projectHoursService.getCurrentProjectHours()();
    const objCode = config.type.toUpperCase();
    
    // Check if cached data exists and matches current item
    if (!cachedHours || !cachedHours.fullHourData) {
      return;
    }

    const isMatch = 
      (objCode === 'TASK' && cachedHours.taskID === itemId) ||
      (objCode === 'PROJECT' && cachedHours.projectID === itemId);
    
    if (!isMatch) {
      return;
    }

    // Create a hash of the data to check if it has changed
    const dataHash = this.createDataHash(cachedHours.fullHourData);
    
    // Only process if data has actually changed
    if (dataHash === this.lastProcessedDataHash) {
      return;
    }

    // Set flag to prevent concurrent processing
    this.isProcessingCacheUpdate = true;
    this.lastProcessedDataHash = dataHash;

    try {
      // Refresh the time summary data with cached data
      // Skip loading state since this is a reactive update from cache
      this.processHourSummaryData(cachedHours.fullHourData, true);
    } finally {
      // Reset flag after a short delay to allow processing to complete
      setTimeout(() => {
        this.isProcessingCacheUpdate = false;
      }, 100);
    }
  }

  /**
   * Create a hash of the hour data to detect changes
   */
  private createDataHash(data: any): string {
    if (!data) return '';
    return `${data.plannedHour || 0}_${data.onsetHours || 0}_${data.offsetHours || 0}`;
  }

  ngOnDestroy(): void {
    this.destroyCharts();
    this.clearTimeouts();
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Calculate task percentage for time summary
  private calculateTaskPercentage(taskData: any): void {
    if (!taskData) {
      this.taskPercentage.set(0);
      return;
    }

    // Calculate percentage based on planned hours and filled hours
    const plannedHours = taskData.plannedHours || 0;
    const filledHours = taskData.hourFilledData || taskData.filledHours || 0;

    if (plannedHours > 0) {
      const percentage = Math.min((filledHours / plannedHours) * 100, 100);
      this.taskPercentage.set(Math.round(percentage));
    } else {
      this.taskPercentage.set(0);
    }
  }

  // Calculate task percentage from API response
  private calculateTaskPercentageFromApi(apiData: any): void {
    if (!apiData) {
      this.taskPercentage.set(0);
      return;
    }

    const plannedHours = apiData.plannedHour || 0;
    const filledHours = apiData.onsetHours || 0;

    if (plannedHours > 0) {
      const percentage = Math.min((filledHours / plannedHours) * 100, 100);
      this.taskPercentage.set(Math.round(percentage));
    } else {
      this.taskPercentage.set(0);
    }
  }

  // Load task time summary data
  private loadTaskTimeSummary(): void {
    if (!this.itemId()) return;

    this.isTimeSummaryLoading.set(true);
    this.isChartsLoading.set(true);

    const config = this.config();
    const objCode = config.type.toUpperCase();
    const objID = this.itemId()!;
    
    // First, check if we have cached data from projectHoursService
    const cachedData = this.projectHoursService.getFullHourData(objID, objCode);
    
    if (cachedData) {
      // Use cached data instead of making API call
      const dataHash = this.createDataHash(cachedData);
      this.lastProcessedDataHash = dataHash;
      this.processHourSummaryData(cachedData);
      return;
    }
    
    // If no cached data, make API call
    const hourDataObservable = this.logtimeService.getHourData(objID, objCode);

    hourDataObservable.pipe(
      catchError(error => {
        console.error(`Error loading ${config.type} time summary:`, error);
        this.isTimeSummaryLoading.set(false);
        this.isChartsLoading.set(false);
        return [];
      })
    ).subscribe((response) => {
      if (response && response.data) {
        // Process the response data
        const dataHash = this.createDataHash(response.data);
        this.lastProcessedDataHash = dataHash;
        this.processHourSummaryData(response.data);
      } else {
        this.isTimeSummaryLoading.set(false);
        this.isChartsLoading.set(false);
      }
    });
  }

  /**
   * Process hour summary data and update component state
   * @param data - The hour summary data from API or cache
   * @param skipLoadingState - If true, don't update loading state (used for cache updates)
   */
  private processHourSummaryData(data: any, skipLoadingState: boolean = false): void {
    // Transform the API response to match our expected format
    const transformedData = {
      totalPlannedHours: data.plannedHour || 0,
      hourFilledData: data.onsetHours || 0,
      unfilledHours: (data.plannedHour || 0) - (data.onsetHours || 0),
      offsetHours: data.offsetHours || 0,
      // Additional fields for user input hours
      totalBillableHours: data.onsetHours || 0,
      totalTaskHours: data.onsetHours || 0,
      totalProjectHours: 0,
      totalRequestHours: 0,
      totalRevisionHours: 0,
      totalNonBillableHours: 0,
      totalIssueHours: 0,
      totalOffsetHours: data.offsetHours || 0
    };
    
    // Check if data has actually changed to avoid unnecessary chart re-initialization
    const currentData = this.taskHourData();
    const hasChanged = !currentData || 
      currentData.hourFilledData !== transformedData.hourFilledData ||
      currentData.totalPlannedHours !== transformedData.totalPlannedHours ||
      currentData.offsetHours !== transformedData.offsetHours;
    
    this.taskHourData.set(transformedData);
    this.taskHourStatus.set(transformedData);
    
    // Calculate task percentage from API response
    this.calculateTaskPercentageFromApi(data);
    
    if (!skipLoadingState) {
      this.isTimeSummaryLoading.set(false);
    }
    
    // Only re-initialize charts if data has changed or charts don't exist
    if (hasChanged || this.chartInstances.length === 0) {
      // Initialize charts after data is loaded with proper delay
      setTimeout(() => {
        this.initializeCharts();
      }, 200);
    }
  }

  // Initialize charts
  private async initializeCharts(): Promise<void> {
    if (!this.taskHourStatus() || !this.taskHourData()) {
      this.isChartsLoading.set(false);
      return;
    }

    try {
      this.destroyCharts();
      this.isChartsLoading.set(true);

      // Add timeout to hide spinner if charts take too long
      this.loadingTimeout = setTimeout(() => {
        if (this.isChartsLoading()) {
          this.isChartsLoading.set(false);
        }
      }, 5000);

      // Wait for DOM to be ready
      await new Promise(resolve => requestAnimationFrame(resolve));
      await new Promise(resolve => setTimeout(resolve, 100));

      // Ensure containers are available
      if (!this.taskStatusChartContainer?.nativeElement || !this.balanceHoursChartContainer?.nativeElement) {
        console.warn('Chart containers not available');
        this.isChartsLoading.set(false);
        return;
      }

      // Initialize task status chart
      this.initTaskStatusChart();
      // Initialize balance hours chart
      this.initBalanceHoursChart();

      this.isChartsLoading.set(false);
      this.clearTimeouts();
    } catch (error) {
      console.error('Error initializing charts:', error);
      this.isChartsLoading.set(false);
      this.hasChartError.set(true);
    }
  }

  // Initialize task status chart
  private initTaskStatusChart(): void {
    if (!this.taskStatusChartContainer?.nativeElement) return;

    const percentage = this.taskPercentage();
    if (percentage === undefined || percentage === null) return;

    try {
      const chartOptions: Highcharts.Options = {
        chart: {
          type: 'solidgauge',
          backgroundColor: 'transparent',
          height: 100,
          width: 100,
          margin: [0, 0, 0, 0],
          spacing: [0, 0, 0, 0]
        },
        title: { text: undefined },
        credits: { enabled: false },
        tooltip: { enabled: false },
        pane: {
          startAngle: -150,
          endAngle: 150,
          background: [{
            outerRadius: '100%',
            innerRadius: '88%',
            backgroundColor: '#A7BBCE',
            shape: 'arc',
            borderWidth: 0
          }]
        },
        yAxis: {
          min: 0,
          max: 100,
          lineWidth: 0,
          tickPositions: [],
          labels: { enabled: false }
        },
        series: [{
          type: 'solidgauge',
          name: 'Completion',
          data: [{
            y: percentage,
            color: '#F46D73',
            radius: '100%',
            innerRadius: '88%'
          }]
        }],
        plotOptions: {
          solidgauge: {
            dataLabels: {
              enabled: true,
              borderWidth: 0,
              useHTML: false,
              y: 0,
              verticalAlign: 'middle',
              align: 'center',
              formatter: function (this: any) {
                return `${this.y}%`;
              },
              style: {
                fontSize: '20px',
                fontWeight: '600',
                color: 'var(--color-text)',
                fontFamily: 'Outfit, sans-serif',
              }
            }
          }
        }
      };

      const chart = Highcharts.chart(this.taskStatusChartContainer.nativeElement, chartOptions);
      this.chartInstances.push(chart);
    } catch (error) {
      console.error('Error creating task status chart:', error);
    }
  }

  // Initialize balance hours chart
  private initBalanceHoursChart(): void {
    if (!this.balanceHoursChartContainer?.nativeElement) return;

    const hourStatus = this.taskHourStatus();
    if (!hourStatus || hourStatus.unfilledHours === undefined) return;

    try {
      const percent = Math.abs(hourStatus.unfilledHours) / (hourStatus.totalPlannedHours || 1) * 100;
      const redArcLength = Math.min(percent, 100);
      

      const chartOptions: Highcharts.Options = {
        chart: {
          type: 'solidgauge',
          backgroundColor: 'transparent',
          height: 100,
          width: 100,
          margin: [0, 0, 0, 0],
          spacing: [0, 0, 0, 0]
        },
        title: { text: undefined },
        credits: { enabled: false },
        tooltip: { enabled: false },
        pane: {
          startAngle: 0,
          endAngle: 360,
          background: [{
            outerRadius: '100%',
            innerRadius: '75%',
            backgroundColor: '#7cc74d',
            shape: 'arc',
            borderWidth: 0
          }]
        },
        yAxis: {
          min: 0,
          max: 100,
          lineWidth: 0,
          tickPositions: [],
          labels: { enabled: false }
        },
        plotOptions: {
          solidgauge: {
            dataLabels: {
              enabled: true,
              borderWidth: 0,
              useHTML: true,
              y: 0,
              verticalAlign: 'middle',
              align: 'center',
              formatter: function (this: any) {
                const unfilledHours = hourStatus.unfilledHours || 0;
                return `<div style="text-align: center; font-family: 'Outfit', sans-serif;">
                          <div style="font-size: 9px; font-weight: 500; line-height: 1.1; color: var(--color-accent-new);">Balance<br>Hours</div>
                          <div style="font-size: 10px; font-weight: 600; color: var(--color-accent-new);">${unfilledHours.toFixed(2)}</div>
                        </div>`;
              }
            },
            linecap: 'round',
            rounded: true,
            stickyTracking: false
          }
        },
        series: [{
          type: 'solidgauge',
          name: 'Balance',
          data: [{
            y: redArcLength,
            color: '#f26666',
            radius: '85%',
            innerRadius: '68%'
          }]
        }]
      };

      const chart = Highcharts.chart(this.balanceHoursChartContainer.nativeElement, chartOptions);
      this.chartInstances.push(chart);
      
      // Add manual text overlay as fallback
      setTimeout(() => {
        this.addBalanceHoursTextOverlay(hourStatus.unfilledHours);
      }, 100);
    } catch (error) {
      console.error('Error creating balance hours chart:', error);
    }
  }

  // Force chart initialization
  private forceChartInitialization(): void {
    this.destroyCharts();
    this.isChartsLoading.set(true);
    setTimeout(() => {
      if (this.selectedTab() === 'time_summary' && this.taskHourStatus()) {
        this.initializeCharts();
      }
    }, 200);
  }

  // Destroy charts
  private destroyCharts(): void {
    this.clearTimeouts();

    this.chartInstances.forEach(chart => {
      try {
        if (chart && chart.destroy && typeof chart.destroy === 'function') {
          chart.destroy();
        }
      } catch (error) {
        console.warn('Error destroying chart:', error);
      }
    });
    this.chartInstances = [];

    if (this.taskStatusChartContainer?.nativeElement) {
      this.taskStatusChartContainer.nativeElement.innerHTML = '';
    }
    if (this.balanceHoursChartContainer?.nativeElement) {
      this.balanceHoursChartContainer.nativeElement.innerHTML = '';
    }
    
    // Remove any text overlays
    const overlays = document.querySelectorAll('.balance-hours-overlay');
    overlays.forEach(overlay => overlay.remove());
  }

  // Clear timeouts
  private clearTimeouts(): void {
    if (this.loadingTimeout) {
      clearTimeout(this.loadingTimeout);
      this.loadingTimeout = null;
    }
    if (this.fallbackTimeout) {
      clearTimeout(this.fallbackTimeout);
      this.fallbackTimeout = null;
    }
  }

  // Add manual text overlay for balance hours chart
  private addBalanceHoursTextOverlay(unfilledHours: number): void {
    if (!this.balanceHoursChartContainer?.nativeElement) return;

    // Remove any existing overlay
    const existingOverlay = this.balanceHoursChartContainer.nativeElement.querySelector('.balance-hours-overlay');
    if (existingOverlay) {
      existingOverlay.remove();
    }


    
  }

  getStatusCardTitle(): string {
    const config = this.config();
    const objectType = config.type.charAt(0).toUpperCase() + config.type.slice(1);
    return `${objectType} Hours And Status`;
  }

  getStatusChartCaption(): string {
    const config = this.config();
    const objectType = config.type.charAt(0).toUpperCase() + config.type.slice(1);
    return `${objectType} Status`;
  }

  // Format hours for display
  formatHours(value: number | null | undefined): string {
    if (value === null || value === undefined || isNaN(value)) {
      return '-';
    }

    const absValue = Math.abs(value);
    const integerPart = Math.floor(absValue).toString();

    if (integerPart.length >= 4) {
      const truncatedInteger = integerPart.substring(0, 4);
      return (value < 0 ? '-' : '') + truncatedInteger;
    }

    const remainingDigits = Math.min(4 - integerPart.length, 2);

    if (remainingDigits <= 0) {
      return (value < 0 ? '-' : '') + integerPart;
    }

    const multiplier = Math.pow(10, remainingDigits);
    const roundedValue = Math.round(absValue * multiplier) / multiplier;

    let result = roundedValue.toFixed(remainingDigits);

    if (result.includes('.')) {
      result = result.replace(/\.?0+$/, '');
    }

    return (value < 0 ? '-' : '') + result;
  }
}
